import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { ENOENT, getLoraxErrorCode } from './connection-manager.js';
import { firstLoraxApiNum, lastFlatApiNum } from './params.js';
import { revisionStringToNumber } from './version-string.js';
export const productCodeInfos = [
    { type: 'pikachu', productCode: 21, modelCodes: [0, 21, 0xffffffff], marketingName: 'OG' }, // Some model codes were not programmed, some were set to 21
    { type: 'pikachu', productCode: 22, modelCodes: [1, 22], marketingName: 'Opal' }, // Some model codes were misprogrammed as 22
    { type: 'pikachu', productCode: 25, modelCodes: [2], marketingName: 'Indiglow' },
    { type: 'pikachu', productCode: 26, modelCodes: [4], marketingName: 'Guardian' },
    { type: 'raichu', productCode: 51, modelCodes: [0, 0xffffffff], marketingName: 'OG' }, // Some model codes were not programmed
    { type: 'peach', productCode: 71, modelCodes: [13], marketingName: 'Onyx' },
    { type: 'peach', productCode: 72, modelCodes: [12], marketingName: 'Pearl' },
    { type: 'peach', productCode: 74, modelCodes: [13, 15], marketingName: 'Desert' }, // Some model codes were misprogrammed as 13
    { type: 'peach', productCode: 75, modelCodes: [17], marketingName: 'Flourish' },
    { type: 'peach', productCode: 78, modelCodes: [19], marketingName: 'Storm' },
    { type: 'peach', productCode: 79, modelCodes: [13], marketingName: 'Onyx' }, // 3DXL bundle version
    { type: 'peach', productCode: 80, modelCodes: [12], marketingName: 'Pearl' }, // 3DXL bundle version
    { type: 'peach', productCode: 81, modelCodes: [23], marketingName: 'Daybreak' },
];
const getProduct = ({ productCode, modelCode, isPup, ledApi }) => {
    const productCodeMatch = productCodeInfos.find((i) => i.productCode === productCode);
    if (productCodeMatch) {
        return productCodeMatch.type;
    }
    // serial number is corrupted or this is an unknown product, try some fallbacks
    if (!isPup) {
        return 'pikachu'; // Pikachu is the only product with the SiLabs bootloader
    }
    if (ledApi === 'led-api-3') {
        return 'peach'; // Peach is the only product with LED API 3 (in FW ≥AF)
    }
    if (ledApi === 'led-api-1') {
        return 'pikachu'; // Pikachu is the only product with LED API 1 (in FW ≤R)
        // Should be impossible to get here; if you do, then something is wrong with PUP detection
    }
    if (modelCode === undefined) {
        return;
    }
    // Some model codes are used on multiple product types; return Pikachu last since it should have been caught by PUP check above
    const modelCodeMatches = productCodeInfos
        .filter((i) => i.modelCodes.includes(modelCode))
        .sort((a, b) => Number(a.type === 'pikachu') - Number(b.type === 'pikachu'));
    return modelCodeMatches[0]?.type;
};
const getProductCodeFromSerialNumber = (serialNumber) => {
    const prefix = (serialNumber ?? '').slice(0, 2);
    if (!/^\d{2}$/.test(prefix)) {
        return;
    }
    return +prefix; // convert to number
};
/** Determine the LED API, even if the serial number and model code are borked */
const getLedApi = async (version, p) => {
    if (version < revisionStringToNumber('T')) {
        return 'led-api-1';
    }
    if (version < revisionStringToNumber('AF')) {
        return 'led-api-2';
    }
    try {
        await p.lorax.heatCycle0PreheatColor.readOnce(); // try to read a file that only exists on LED API 2
        return 'led-api-2';
    }
    catch (err) {
        if (getLoraxErrorCode(err) === ENOENT) {
            return 'led-api-3'; // file does not exist, this is LED API 3
        }
        throw err;
    }
};
export class DeviceInfoReader {
    ota;
    logger;
    conn;
    unlocker;
    subj = new BehaviorSubject(undefined);
    subscriptions = [];
    params;
    /**
     * @param ota `OtaManager` instance for the connection
     * @param logger `Logger` to use for errors
     * @param conn `ConnectionManager` instance for the connection
     * @param unlocker `AccessUnlocker` instance for the connection
     */
    constructor(ota, logger, conn, unlocker) {
        this.ota = ota;
        this.logger = logger;
        this.conn = conn;
        this.unlocker = unlocker;
        this.subscriptions.push(this.infoObs.pipe(distinctUntilChanged()).subscribe((version) => {
            if (version) {
                this.logger.log('API version', { ...version });
            }
            else {
                this.logger.log('API version cleared');
            }
        }));
    }
    /** Connect the ParamRegistry once it's constructed */
    setup(paramReg) {
        if (this.params) {
            throw new Error('Multiple calls to DeviceInfoReader#setup');
        }
        this.params = this.getParams(paramReg);
        this.subscriptions.push(this.conn.deviceStateObs
            .pipe(filter((v) => v !== 'connected'))
            .subscribe(this.onDisconnected));
    }
    /** Close all subscriptions */
    teardown() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    /** Device info, or undefined if not connected/not successfully read */
    get info() {
        return this.subj.value;
    }
    /** `Observable` of the values obtainable from `info` */
    get infoObs() {
        return this.subj.asObservable();
    }
    /** Return the first param in the supplied array that matches the API of the connected device */
    findInApi = (params) => params.find((p) => p.isInApi(this.info?.apiVersion, this.info?.product));
    /**
     * Check whether the device is in the application or bootloader, and which bootloader protocol it uses.
     * Also reads params that don't require unlock.
     * @returns Value of `deviceState`
     */
    readInfo = async () => {
        if (!this.params) {
            throw new Error('DeviceInfoReader not set up');
        }
        const unlockedInfo = this.unlocker.unlocked;
        if (!unlockedInfo) {
            throw new Error('Device not unlocked');
        }
        const p = this.conn.isLoraxConnected ? this.params.lorax : this.params.flat;
        let serialNumber;
        let serialNumberSource;
        let ledApi;
        let modelCode;
        let productCode;
        try {
            this.logger.log(`Reading FW API version`);
            serialNumber = this.ota.serialNumber;
            if (serialNumber) {
                serialNumberSource = 'ota';
            }
            else if (p.serialNumber.isInApi(unlockedInfo.apiVersion, undefined)) {
                serialNumberSource = 'api';
                serialNumber = await p.serialNumber.readOnce();
            }
            ledApi = await getLedApi(unlockedInfo.apiVersion, this.params);
            // Peak Pros with FW <K lack the mirror characteristic for model code, but all of them are the base model (0)
            const modelCodeRead = p.model.isInApi(unlockedInfo.apiVersion, undefined)
                ? await p.model.readOnce()
                : 0;
            modelCode = modelCodeRead;
            productCode = getProductCodeFromSerialNumber(serialNumber);
            const product = getProduct({
                productCode,
                modelCode,
                isPup: this.ota.isConnectedPup,
                ledApi,
            });
            const productCodeInfo = productCodeInfos.find((i) => i.type === product &&
                i.productCode === productCode &&
                i.modelCodes.includes(modelCodeRead));
            const info = {
                apiVersion: unlockedInfo.apiVersion,
                serialNumber,
                modelCode,
                product,
                isConsistent: !!productCodeInfo,
                marketingName: productCodeInfo?.marketingName,
                features: [ledApi],
            };
            this.subj.next(info);
            return info;
        }
        catch (error) {
            this.logger.warn('Failed to read device info', {
                error,
                serialNumber,
                serialNumberSource,
                ledApi,
                productCode,
            });
            this.subj.next(undefined);
            throw error;
        }
    };
    onDisconnected = () => {
        this.subj.next(undefined);
    };
    getParams(paramReg) {
        const versionArr = paramReg.get('swApiVersion');
        const flatVersion = versionArr.find((p) => p.isInApi(lastFlatApiNum, undefined));
        const loraxVersion = versionArr.find((p) => p.isInApi(firstLoraxApiNum, undefined));
        if (!flatVersion || !loraxVersion) {
            throw new Error(`Could not find API version param`);
        }
        const serialNumberArr = paramReg.get('serialNumber');
        const flatSerialNumber = serialNumberArr.find((p) => p.isInApi(lastFlatApiNum, undefined));
        const loraxSerialNumber = serialNumberArr.find((p) => p.isInApi(firstLoraxApiNum, undefined));
        if (!flatSerialNumber || !loraxSerialNumber) {
            throw new Error(`Could not find serial number param`);
        }
        const modelArr = paramReg.get('modelCode');
        const flatModel = modelArr.find((p) => p.isInApi(lastFlatApiNum, undefined));
        const loraxModel = modelArr.find((p) => p.isInApi(firstLoraxApiNum, undefined));
        if (!flatModel || !loraxModel) {
            throw new Error(`Could not find model code param`);
        }
        const ledApi2TestArr = paramReg.get('heatCycle0PreheatColorApi2');
        const loraxHeatCycle0PreheatColor = ledApi2TestArr.find((p) => p.isInApi(firstLoraxApiNum, undefined));
        if (!loraxHeatCycle0PreheatColor) {
            throw new Error(`Could not find LED API 2 test param`);
        }
        return {
            flat: {
                apiVersion: flatVersion,
                serialNumber: flatSerialNumber,
                model: flatModel,
            },
            lorax: {
                apiVersion: loraxVersion,
                serialNumber: loraxSerialNumber,
                model: loraxModel,
                heatCycle0PreheatColor: loraxHeatCycle0PreheatColor,
            },
        };
    }
}
//# sourceMappingURL=device-info-reader.js.map